import React, { useState, useEffect } from 'react';
import { BringzzButton, BringzzText } from "@bringzz/components";
import { useNavigation } from 'context/NavigationContext';
import {
    BringzzPageContainer,
    BringzzIcon,
    BringzzRadioList,
    BringzzRadio
} from "@bringzz/components";
import SOFORT_IMAGE from "assets/methods/sofort.png";
import { requestLocationPermission } from 'location';
import useRequest from "hooks/useRequest";
import { useTranslation } from 'react-i18next';
import { useAuth } from 'context/AuthContext';

const ConsumerAddressPage = () => {
    const { backPress } = useNavigation();
    const { navigate } = useNavigation();
    const { sendRequest } = useRequest();
    const { t } = useTranslation();
    const [error, setError] = useState(null);
    const [isLocating, setIsLocating] = useState(false);
    const [locationError, setLocationError] = useState(null);
    const [userAddress, setUserAddress] = useState("");
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [addresses, setAddresses] = useState([]);
    const [activeAddress, setActiveAddress] = useState(null);
    const { user, updateUserAddresses } = useAuth();
    
    useEffect(() => {
        const initMapKit = async () => {
            if (!window.mapkit) {
                const script = document.createElement('script');
                script.src = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js';
                document.head.appendChild(script);
                await new Promise((resolve) => { script.onload = resolve; });
            }
            if (!window.mapkit.isInitialized) {
                window.mapkit.init({
                    authorizationCallback: (done) => {
                        done(process.env.REACT_APP_APPLE_MAP_KEY);
                    },
                });
            }
        };
        initMapKit();
    }, []);
    
    let state = {
        checked: (
            <BringzzIcon
                icon="IconCheck"
                library="Custom"
                className="h-5 w-5 p-1 bg-magic-lilac text-light rounded-full"
                size="12"
            />
        ),
        unChecked: (
            <div className="h-5 w-5 rounded-full border border-neutral-400"></div>
        ),
    };
    const handleUseCurrentLocation = async () => {
        setLocationError(null);
        setSelectedMethod("current");
        
        if (!navigator.geolocation) {
            setLocationError(t('component.auth.consumer.address.errors.geolocation_unsupported'));
            return;
        }

        setIsLocating(true);
        
        try {
            const position = await new Promise((resolve, reject) => {
                const timeoutId = setTimeout(() => {
                    reject(new Error('Location request timed out. Please try again.'));
                }, 15000);
                
                navigator.geolocation.getCurrentPosition(
                    (pos) => {
                        clearTimeout(timeoutId);
                        resolve(pos);
                    },
                    (err) => {
                        clearTimeout(timeoutId);
                        reject(err);
                    },
                    {
                        enableHighAccuracy: true,
                        timeout: 10000,
                        maximumAge: 60000
                    }
                );
            });

            const geocoder = new window.mapkit.Geocoder();
            const coordinate = new window.mapkit.Coordinate(position.coords.latitude, position.coords.longitude);
            
            const geocodeResult = await new Promise((resolve, reject) => {
                geocoder.reverseLookup(coordinate, (error, data) => {
                    if (error) {
                        console.error('MapKit geocoding error:', error);
                        reject(error);
                    } else {
                        resolve(data.results[0]);
                    }
                });
            });

            let postalCode = geocodeResult.postalCode || '';
            let streetName = geocodeResult.thoroughfare || '';
            let houseNumber = geocodeResult.subThoroughfare || '';
            let cityName = geocodeResult.locality || geocodeResult.administrativeArea || '';
            let countryName = geocodeResult.country || '';

            if (!postalCode) {
                try {
                    const nominatimResponse = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.coords.latitude}&lon=${position.coords.longitude}&addressdetails=1`
                    );
                    const nominatimData = await nominatimResponse.json();
                    
                    if (nominatimData.address) {
                        postalCode = nominatimData.address.postcode || '';
                        if (!streetName) streetName = nominatimData.address.road || '';
                    }
                } catch (nominatimError) {
                    console.warn('Nominatim geocoding failed:', nominatimError);
                }
            }

            const response = await sendRequest({
                method: "POST",
                url: "/module/address/setLocationAddress",
                data: {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    streetName,
                    houseNumber,
                    cityName,
                    postalCode,
                    countryName,
                }
            });

            if (response.status === "success") {
                // setLocationAddress automatically sets as active, no need for separate call
                // Update user addresses in context
                if (updateUserAddresses) {
                    await updateUserAddresses();
                }
            }
        } catch (error) {
            console.error("Location error:", error);
            let errorMessage = "Could not detect your location";
            
            if (error.code === 1) {
                errorMessage = "Location access denied. Please enable location permissions.";
            } else if (error.code === 2) {
                errorMessage = "Location unavailable. Please try again.";
            } else if (error.code === 3 || error.message?.includes('timeout')) {
                errorMessage = "Location request timed out. Please try again.";
            }
            
            setLocationError(errorMessage);
        } finally {
            setIsLocating(false);
        }
    };

    const fetchAddresses = async () => {
        try {
            // Update addresses in context
            if (updateUserAddresses) {
                await updateUserAddresses();
            }
        } catch (error) {
            console.error('Failed to fetch addresses:', error);
        }
    };

    const setActiveAddressHandler = async (integrationId) => {
        try {
            const response = await sendRequest({
                method: 'POST',
                url: '/module/address/setActiveAddress',
                data: { integrationId },
            });
            if (response.status === 'success') {
                await fetchAddresses();
                if (updateUserAddresses) {
                    await updateUserAddresses();
                }
            }
        } catch (error) {
            console.error('Failed to set active address:', error);
        }
    };

    useEffect(() => {
        fetchAddresses();
    }, []);
    
    // Update local state when user context changes
    useEffect(() => {
        if (user?.addresses) {
            setAddresses(user.addresses);
        }
        if (user?.activeAddress) {
            setActiveAddress(user.activeAddress);
            setUserAddress(`${user.activeAddress.streetName || ''}, ${user.activeAddress.cityName || ''}, ${user.activeAddress.postalCode || ''}`);
        } else {
            setActiveAddress(null);
            setUserAddress('');
        }
    }, [user?.addresses, user?.activeAddress]);

    const handleContinue = async () => {
        // If we have an active address, proceed to next step
        if (activeAddress) {
            navigate("/register/notification");
        }
    };

    return (
        <BringzzPageContainer
            className="bg-white"
            steps={true}
            activeStep={4}
            header={{
                left: (
                    <BringzzIcon
                        size='22'
                        icon='IconChevronLeft'
                        folder='LineIcons'
                        className='cursor-pointer'
                        onClick={backPress}
                    />
                ),
                title: t('component.auth.consumer.address.header.title'),
                right: <BringzzButton size='sm' className="border border-black" onClick={() => {
                    navigate('/register/notification');
                }}>{t('component.auth.consumer.address.header.skip')}</BringzzButton>
            }}
            footer={
                <div className='pb-8 px-6'>
                    <BringzzButton
                        size='lg'
                        className='bg-magic-lilac w-full flex justify-center text-midnight-blue'
                        onClick={handleContinue}
                        disabled={!activeAddress || isLocating}
                    >
                        {isLocating ? t('component.auth.consumer.address.buttons.saving_location') : t('component.auth.consumer.address.buttons.continue')}
                    </BringzzButton>
                </div>
            }
            authenticated={false}
        >
            <div className='relative flex justify-center bg-magic-lilac/20'>
                <div className='relative bg-white w-full bg-cover bg-center flex flex-col'>
                    {/* Content */}
                    <div className='flex-grow flex flex-col py-8 px-4'>
                        <div className='z-10 flex flex-col '>
                            <div className='pl-2 flex flex-col space-y-6 md:max-w-lg lg:max-w-xl xl:max-w-2xl'>
                                <BringzzText tag='h1' className='pl-2 text-midnight-blue font-bold'>
                                    {t('component.auth.consumer.address.title')}
                                </BringzzText>
                                <BringzzText tag="h3" className="font-normal">{t('component.auth.consumer.address.subtitle')}</BringzzText>
                            </div>
                            <div className='mt-8'>
                                <BringzzRadioList
                                    onChange={(newValue) => setSelectedMethod(newValue)}
                                    state={state}
                                    className="divide-y border-t"
                                >
                                    <div
                                        className={`flex items-center justify-start gap-x-4 p-4 min-h-[75px] cursor-pointer ${
                                            isLocating ? 'opacity-75 pointer-events-none' : 'hover:bg-gray-50'
                                        }`}
                                        onClick={handleUseCurrentLocation}
                                    >
                                        <BringzzIcon
                                            icon="IconStar"
                                            folder="LineIcons"
                                            className="flex items-center w-10 justify-center rounded-full"
                                            size='24'
                                        />
                                        <div>
                                            <BringzzText tag="h3">
                                                {isLocating ? 'Detecting your location...' : t('component.auth.consumer.address.options.use_current')}
                                            </BringzzText>
                                            {locationError && (
                                                <BringzzText tag='h5' className='text-red-500'>
                                                    {locationError}
                                                </BringzzText>
                                            )}                      
                                        </div>
                                    </div>
                                    <div
                                        className="flex items-center justify-start gap-x-4 p-4 min-h-[75px] cursor-pointer hover:bg-gray-50 mt-4"
                                        onClick={() => navigate("/register/consumer/location", { state: { returnTo: '/register/consumer/address' } })}
                                    >
                                        <BringzzIcon
                                            icon="IconStar"
                                            folder="LineIcons"
                                            className="flex items-center w-10 justify-center rounded-full"
                                            size='24'
                                        />
                                        <div>
                                            <BringzzText tag="h3">{t('component.auth.consumer.address.options.add_new')}</BringzzText>
                                        </div>
                                    </div>
                                </BringzzRadioList>
                                
                                {addresses.length > 0 && (
                                    <div className="mt-6 space-y-3">
                                        {addresses.map((address) => (
                                            <div key={address.integrationId} className={`flex items-center justify-between gap-x-4 p-4 min-h-[75px] rounded-md border ${
                                                activeAddress?.integrationId === address.integrationId 
                                                    ? 'border-blue-500 bg-blue-50' 
                                                    : 'border-gray-200 bg-white'
                                            }`}>
                                                <div className="flex items-center gap-x-4 flex-1 cursor-pointer" onClick={() => setActiveAddressHandler(address.integrationId)}>
                                                    <BringzzIcon 
                                                        folder="LineIcons" 
                                                        icon={address.addressIcon || "IconHome"} 
                                                        size="20" 
                                                        className={activeAddress?.integrationId === address.integrationId ? "text-blue-600" : "text-gray-600"} 
                                                    />
                                                    <div className="flex flex-col">
                                                        <BringzzText tag="p" className="font-regular text-midnight-blue">
                                                            {address.streetName}, {address.cityName}, {address.postalCode}
                                                        </BringzzText>
                                                        <BringzzText tag="p" className="text-sm text-gray-500">
                                                            {address.addressLable || "Address"}
                                                        </BringzzText>
                                                    </div>
                                                </div>
                                                <BringzzIcon 
                                                    folder="LineIcons" 
                                                    icon="IconPencil" 
                                                    size="20" 
                                                    className="text-gray-600 cursor-pointer hover:text-blue-600" 
                                                    onClick={() => navigate("/register/consumer/location", { 
                                                        state: { 
                                                            returnTo: '/register/consumer/address',
                                                            editAddress: address
                                                        } 
                                                    })}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </BringzzPageContainer>
    );
};

export default ConsumerAddressPage;
